import {ResourceStatus} from '@/shared/components/ui/resource-status'
import {AccessorColDef, DisplayColDef} from '@cypro/ui/types'
import {chakra, Checkbox} from '@chakra-ui/react'
import {DrugCatalogItem} from '@cypro/engine/schema'
import {createColumnHelper} from '@tanstack/react-table'
import {useTranslate} from '@tolgee/react'

export const drugItemColumnsHelper = createColumnHelper<DrugCatalogItem>()

type AccessorColumnDef = AccessorColDef<DrugCatalogItem>
type DisplayColumnDef = DisplayColDef<DrugCatalogItem>

export const DrugItemColumnsDef = {
  select: (def?: DisplayColumnDef) =>
    drugItemColumnsHelper.display({
      id: 'select',
      minSize: 42, // px
      header: ({table}) => {
        const allSelected = table.getIsAllRowsSelected()
        const isIndeterminate = table.getIsSomeRowsSelected() && !allSelected

        return (
          <Checkbox.Root
            size="md"
            aria-label="Select row"
            checked={isIndeterminate ? 'indeterminate' : allSelected}
            onCheckedChange={changes => table.toggleAllRowsSelected(Boolean(changes.checked))}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        )
      },
      cell: ({row}) => {
        return (
          <chakra.div onClick={e => e.stopPropagation()}>
            <Checkbox.Root
              size="md"
              aria-label="Select row"
              checked={row.getIsSelected()}
              onCheckedChange={changes => row.toggleSelected(Boolean(changes.checked))}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
            </Checkbox.Root>
          </chakra.div>
        )
      },
      enableSorting: false,
      ...def,
    }),

  code: (def?: AccessorColumnDef) =>
    drugItemColumnsHelper.accessor('code', {
      header: () => {
        const {t} = useTranslate('common')
        return t('ID')
      },
      cell: ({row}) => {
        return <chakra.div color="fg.muted">{row.original.code}</chakra.div>
      },
      ...def,
    }),

  name: (def?: AccessorColumnDef) =>
    drugItemColumnsHelper.accessor('name', {
      header: () => {
        const {t} = useTranslate('common')
        return t('name')
      },
      ...def,
    }),

  description: (def?: AccessorColumnDef) =>
    drugItemColumnsHelper.accessor('description', {
      header: () => {
        const {t} = useTranslate('common')
        return t('description')
      },
      cell: ({row}) => <chakra.div color="fg.muted">{row.original.description}</chakra.div>,
      ...def,
    }),

  categories: (def?: DisplayColumnDef) =>
    drugItemColumnsHelper.display({
      id: 'categories',
      header: () => {
        const {t} = useTranslate('common')
        return <chakra.div>{t('categories')}</chakra.div>
      },
      cell: ({row}) => {
        const {t} = useTranslate('common')
        const parentCategories = row.original.categories
          ?.map(c => (c.drugCategory?.parentId ? null : c.drugCategory?.name))
          ?.filter(Boolean)

        return <chakra.div color="fg.muted">{parentCategories?.join(', ') || t('no_categories')}</chakra.div>
      },
      ...def,
    }),

  status: (def?: DisplayColumnDef) =>
    drugItemColumnsHelper.display({
      id: 'status',
      header: () => {
        const {t} = useTranslate('common')
        return (
          <chakra.div display="flex" justifyContent="flex-end" width="100%">
            {t('status')}
          </chakra.div>
        )
      },
      cell: ({row}) => (
        <chakra.div display="flex" justifyContent="flex-end" width="100%">
          <ResourceStatus status={row.original.status} />
        </chakra.div>
      ),
      enableSorting: false,
      ...def,
    }),
}
